import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import {
  Loader2,
  Plus,
  Trash2,
  Target,
  Calendar,
  DollarSign,
  Users,
  MessageSquare,
  CheckCircle2
} from 'lucide-react';

const STATUS_CONFIG = {
  planning: { label: 'Planning', color: 'bg-slate-100 text-slate-700' },
  active: { label: 'Active', color: 'bg-blue-50 text-blue-700' },
  on_hold: { label: 'On Hold', color: 'bg-amber-50 text-amber-700' },
  completed: { label: 'Completed', color: 'bg-emerald-50 text-emerald-700' }
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Medium', color: 'bg-blue-50 text-blue-600' },
  high: { label: 'High', color: 'bg-amber-50 text-amber-600' },
  critical: { label: 'Critical', color: 'bg-red-50 text-red-600' }
};

const CAMPAIGN_TYPES = [
  'Product Launch',
  'Brand Awareness',
  'Lead Generation',
  'Customer Retention',
  'Seasonal Campaign',
  'Event Promotion',
  'Content Series',
  'Partnership/Co-marketing',
  'Other'
];

const BUSINESS_OBJECTIVES = [
  'Revenue Generation',
  'Market Expansion',
  'Brand Building',
  'Customer Acquisition',
  'Customer Retention',
  'Product Adoption',
  'Thought Leadership',
  'Market Research',
  'Other'
];

const TONE_OPTIONS = [
  'Professional',
  'Casual/Friendly',
  'Urgent/Action-oriented',
  'Inspirational',
  'Educational',
  'Playful/Fun',
  'Authoritative',
  'Empathetic'
];

const INITIAL_FORM_STATE = {
  // Project Overview
  name: '',
  description: '',
  owner_id: '',
  department: '',
  campaign_type: '',
  business_objective: '',
  strategic_alignment: '',
  status: 'planning',
  priority: 'medium',
  // Timeline
  start_date: '',
  launch_date: '',
  end_date: '',
  milestones: [],
  // Budget
  total_budget: '',
  allocated_spend: [],
  projected_roi: '',
  actual_roi: '',
  // Audience
  primary_audience: '',
  secondary_audience: '',
  buyer_persona: '',
  geography: '',
  industry_segment: '',
  // Messaging
  value_proposition: '',
  key_messages: [],
  call_to_action: '',
  tone_of_voice: '',
  brand_guidelines: ''
};

export const ProjectForm = ({ 
  initialData = null, 
  users = [], 
  onSubmit, 
  onCancel, 
  isEditing = false,
  currentUserId = null
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [newMilestone, setNewMilestone] = useState({ name: '', target_date: '' });
  const [newSpendCategory, setNewSpendCategory] = useState({ category: '', amount: '' });
  const [newMessage, setNewMessage] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...INITIAL_FORM_STATE,
        ...initialData,
        total_budget: initialData.total_budget || '',
        projected_roi: initialData.projected_roi || '',
        actual_roi: initialData.actual_roi || '',
        milestones: initialData.milestones || [],
        allocated_spend: initialData.allocated_spend || [],
        key_messages: initialData.key_messages || []
      });
    }
  }, [initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        owner_id: formData.owner_id || currentUserId,
        total_budget: formData.total_budget ? parseFloat(formData.total_budget) : null,
        projected_roi: formData.projected_roi ? parseFloat(formData.projected_roi) : null,
        actual_roi: formData.actual_roi ? parseFloat(formData.actual_roi) : null
      };
      await onSubmit(submitData);
    } catch (error) {
      console.error('Failed to save project:', error);
    } finally {
      setSaving(false);
    }
  };

  const addMilestone = () => {
    if (newMilestone.name) {
      const milestone = {
        id: `ms-${Date.now()}`,
        name: newMilestone.name,
        target_date: newMilestone.target_date || null,
        completed: false
      };
      setFormData(prev => ({
        ...prev,
        milestones: [...prev.milestones, milestone]
      }));
      setNewMilestone({ name: '', target_date: '' });
    }
  };

  const removeMilestone = (id) => {
    setFormData(prev => ({
      ...prev,
      milestones: prev.milestones.filter(m => m.id !== id)
    }));
  };

  const toggleMilestone = (id) => {
    setFormData(prev => ({
      ...prev,
      milestones: prev.milestones.map(m => 
        m.id === id ? { ...m, completed: !m.completed } : m
      )
    }));
  };

  const addSpendCategory = () => {
    if (newSpendCategory.category && newSpendCategory.amount) {
      setFormData(prev => ({
        ...prev,
        allocated_spend: [...prev.allocated_spend, {
          category: newSpendCategory.category,
          amount: parseFloat(newSpendCategory.amount)
        }]
      }));
      setNewSpendCategory({ category: '', amount: '' });
    }
  };

  const removeSpendCategory = (index) => {
    setFormData(prev => ({
      ...prev,
      allocated_spend: prev.allocated_spend.filter((_, i) => i !== index)
    }));
  };

  const addKeyMessage = () => {
    if (newMessage.trim()) {
      setFormData(prev => ({
        ...prev,
        key_messages: [...prev.key_messages, newMessage.trim()]
      }));
      setNewMessage('');
    }
  };

  const removeKeyMessage = (index) => {
    setFormData(prev => ({
      ...prev,
      key_messages: prev.key_messages.filter((_, i) => i !== index)
    }));
  };

  const totalAllocated = formData.allocated_spend.reduce((sum, item) => sum + (item.amount || 0), 0);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5 mb-4">
          <TabsTrigger value="overview" className="flex items-center gap-1.5 text-xs" data-testid="tab-overview">
            <Target className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="timeline" className="flex items-center gap-1.5 text-xs" data-testid="tab-timeline">
            <Calendar className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Timeline</span>
          </TabsTrigger>
          <TabsTrigger value="budget" className="flex items-center gap-1.5 text-xs" data-testid="tab-budget">
            <DollarSign className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Budget</span>
          </TabsTrigger>
          <TabsTrigger value="audience" className="flex items-center gap-1.5 text-xs" data-testid="tab-audience">
            <Users className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Audience</span>
          </TabsTrigger>
          <TabsTrigger value="messaging" className="flex items-center gap-1.5 text-xs" data-testid="tab-messaging">
            <MessageSquare className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Messaging</span>
          </TabsTrigger>
        </TabsList>

        <ScrollArea className="h-[400px] pr-4">
          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4 mt-0">
            <div className="space-y-2">
              <Label>Project Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Q1 Brand Campaign"
                required
                data-testid="project-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the project goals, scope, and expected outcomes..."
                rows={3}
                data-testid="project-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Campaign Type</Label>
                <Select 
                  value={formData.campaign_type || "none"} 
                  onValueChange={(v) => setFormData({ ...formData, campaign_type: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="campaign-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select type</SelectItem>
                    {CAMPAIGN_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Business Objective</Label>
                <Select 
                  value={formData.business_objective || "none"} 
                  onValueChange={(v) => setFormData({ ...formData, business_objective: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="business-objective-select">
                    <SelectValue placeholder="Select objective" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select objective</SelectItem>
                    {BUSINESS_OBJECTIVES.map(obj => (
                      <SelectItem key={obj} value={obj}>{obj}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Strategic Alignment</Label>
              <Textarea
                value={formData.strategic_alignment}
                onChange={(e) => setFormData({ ...formData, strategic_alignment: e.target.value })}
                placeholder="How does this project align with company goals and OKRs?"
                rows={2}
                data-testid="strategic-alignment-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Owner</Label>
                <Select 
                  value={formData.owner_id || "self"} 
                  onValueChange={(v) => setFormData({ ...formData, owner_id: v === "self" ? "" : v })}
                >
                  <SelectTrigger data-testid="project-owner-select">
                    <SelectValue placeholder="Select owner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Self (Current User)</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Input
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  placeholder="Marketing"
                  data-testid="project-department-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger data-testid="project-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger data-testid="project-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline" className="space-y-4 mt-0">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  data-testid="project-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Launch Date</Label>
                <Input
                  type="date"
                  value={formData.launch_date}
                  onChange={(e) => setFormData({ ...formData, launch_date: e.target.value })}
                  data-testid="project-launch-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  data-testid="project-end-date"
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label>Key Milestones</Label>
              <div className="flex gap-2">
                <Input
                  value={newMilestone.name}
                  onChange={(e) => setNewMilestone({ ...newMilestone, name: e.target.value })}
                  placeholder="Milestone name (e.g., Creative Approved)"
                  className="flex-1"
                  data-testid="milestone-name-input"
                />
                <Input
                  type="date"
                  value={newMilestone.target_date}
                  onChange={(e) => setNewMilestone({ ...newMilestone, target_date: e.target.value })}
                  className="w-40"
                  data-testid="milestone-date-input"
                />
                <Button type="button" size="icon" onClick={addMilestone} data-testid="add-milestone-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {formData.milestones.length > 0 && (
                <div className="space-y-2 mt-3">
                  {formData.milestones.map((milestone) => (
                    <div 
                      key={milestone.id} 
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                      data-testid={`milestone-${milestone.id}`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleMilestone(milestone.id)}
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                          milestone.completed 
                            ? 'bg-emerald-500 border-emerald-500 text-white' 
                            : 'border-slate-300 hover:border-slate-400'
                        }`}
                      >
                        {milestone.completed && <CheckCircle2 className="w-3 h-3" />}
                      </button>
                      <div className="flex-1">
                        <p className={`font-medium ${milestone.completed ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                          {milestone.name}
                        </p>
                        {milestone.target_date && (
                          <p className="text-xs text-slate-500">{milestone.target_date}</p>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400 hover:text-red-500"
                        onClick={() => removeMilestone(milestone.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Budget Tab */}
          <TabsContent value="budget" className="space-y-4 mt-0">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Total Budget ($)</Label>
                <Input
                  type="number"
                  value={formData.total_budget}
                  onChange={(e) => setFormData({ ...formData, total_budget: e.target.value })}
                  placeholder="50000"
                  data-testid="total-budget-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Allocated: ${totalAllocated.toLocaleString()}</Label>
                {formData.total_budget && (
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all ${totalAllocated > parseFloat(formData.total_budget) ? 'bg-red-500' : 'bg-emerald-500'}`}
                      style={{ width: `${Math.min((totalAllocated / parseFloat(formData.total_budget)) * 100, 100)}%` }}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <Label>Budget Allocation</Label>
              <div className="flex gap-2">
                <Input
                  value={newSpendCategory.category}
                  onChange={(e) => setNewSpendCategory({ ...newSpendCategory, category: e.target.value })}
                  placeholder="Category (e.g., Paid Media)"
                  className="flex-1"
                  data-testid="spend-category-input"
                />
                <Input
                  type="number"
                  value={newSpendCategory.amount}
                  onChange={(e) => setNewSpendCategory({ ...newSpendCategory, amount: e.target.value })}
                  placeholder="Amount"
                  className="w-32"
                  data-testid="spend-amount-input"
                />
                <Button type="button" size="icon" onClick={addSpendCategory} data-testid="add-spend-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {formData.allocated_spend.length > 0 && (
                <div className="space-y-2 mt-3">
                  {formData.allocated_spend.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="flex-1 flex justify-between items-center">
                        <span className="font-medium text-slate-700">{item.category}</span>
                        <span className="text-slate-600">${item.amount.toLocaleString()}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400 hover:text-red-500"
                        onClick={() => removeSpendCategory(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Projected ROI (%)</Label>
                <Input
                  type="number"
                  value={formData.projected_roi}
                  onChange={(e) => setFormData({ ...formData, projected_roi: e.target.value })}
                  placeholder="150"
                  data-testid="projected-roi-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Actual ROI (%)</Label>
                <Input
                  type="number"
                  value={formData.actual_roi}
                  onChange={(e) => setFormData({ ...formData, actual_roi: e.target.value })}
                  placeholder="125"
                  data-testid="actual-roi-input"
                />
              </div>
            </div>
          </TabsContent>

          {/* Audience Tab */}
          <TabsContent value="audience" className="space-y-4 mt-0">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Primary Audience</Label>
                <Input
                  value={formData.primary_audience}
                  onChange={(e) => setFormData({ ...formData, primary_audience: e.target.value })}
                  placeholder="e.g., Enterprise IT Decision Makers"
                  data-testid="primary-audience-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Secondary Audience</Label>
                <Input
                  value={formData.secondary_audience}
                  onChange={(e) => setFormData({ ...formData, secondary_audience: e.target.value })}
                  placeholder="e.g., SMB Business Owners"
                  data-testid="secondary-audience-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Buyer Persona</Label>
              <Textarea
                value={formData.buyer_persona}
                onChange={(e) => setFormData({ ...formData, buyer_persona: e.target.value })}
                placeholder="Describe your ideal buyer: demographics, pain points, goals, decision-making process..."
                rows={3}
                data-testid="buyer-persona-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Geography</Label>
                <Input
                  value={formData.geography}
                  onChange={(e) => setFormData({ ...formData, geography: e.target.value })}
                  placeholder="e.g., North America, EMEA"
                  data-testid="geography-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Industry Segment</Label>
                <Input
                  value={formData.industry_segment}
                  onChange={(e) => setFormData({ ...formData, industry_segment: e.target.value })}
                  placeholder="e.g., Healthcare, FinTech"
                  data-testid="industry-segment-input"
                />
              </div>
            </div>
          </TabsContent>

          {/* Messaging Tab */}
          <TabsContent value="messaging" className="space-y-4 mt-0">
            <div className="space-y-2">
              <Label>Core Value Proposition</Label>
              <Textarea
                value={formData.value_proposition}
                onChange={(e) => setFormData({ ...formData, value_proposition: e.target.value })}
                placeholder="What unique value does this campaign promise to deliver to your audience?"
                rows={3}
                data-testid="value-proposition-input"
              />
            </div>

            <div className="space-y-3">
              <Label>Key Message Pillars</Label>
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Add a key message pillar"
                  className="flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyMessage())}
                  data-testid="key-message-input"
                />
                <Button type="button" size="icon" onClick={addKeyMessage} data-testid="add-message-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {formData.key_messages.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.key_messages.map((msg, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="flex items-center gap-1 py-1.5 px-3"
                    >
                      {msg}
                      <button
                        type="button"
                        onClick={() => removeKeyMessage(index)}
                        className="ml-1 hover:text-red-500"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Primary Call-to-Action</Label>
              <Input
                value={formData.call_to_action}
                onChange={(e) => setFormData({ ...formData, call_to_action: e.target.value })}
                placeholder="e.g., Schedule a Demo, Start Free Trial"
                data-testid="cta-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Tone of Voice</Label>
              <Select 
                value={formData.tone_of_voice || "none"} 
                onValueChange={(v) => setFormData({ ...formData, tone_of_voice: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="tone-select">
                  <SelectValue placeholder="Select tone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select tone</SelectItem>
                  {TONE_OPTIONS.map(tone => (
                    <SelectItem key={tone} value={tone}>{tone}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Brand Guidelines Notes</Label>
              <Textarea
                value={formData.brand_guidelines}
                onChange={(e) => setFormData({ ...formData, brand_guidelines: e.target.value })}
                placeholder="Any specific brand guidelines, color usage, logo placement, or style requirements..."
                rows={3}
                data-testid="brand-guidelines-input"
              />
            </div>
          </TabsContent>
        </ScrollArea>
      </Tabs>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving} data-testid="save-project-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {isEditing ? 'Update' : 'Create'} Project
        </Button>
      </div>
    </form>
  );
};
